/**
 * ConfirmStageModal — the two ChannelMeter human-gate confirmations
 * (upload / payment lock), on ui/Modal. These record that an EXTERNAL action
 * was done in ChannelMeter; the lock confirm is irreversible and uses the
 * spark (confirm-warning) button per DS §3.1.
 */

import React, { useEffect, useState } from 'react';
import { Button, Modal } from '../../ui';

export type ConfirmStep = 'cmUpload' | 'cmLock';

interface ConfirmStageModalProps {
  step: ConfirmStep | null;
  monthDisplay: string;
  busy: boolean;
  onConfirm: (notes?: string) => void;
  onClose: () => void;
}

const ConfirmStageModal: React.FC<ConfirmStageModalProps> = ({
  step,
  monthDisplay,
  busy,
  onConfirm,
  onClose,
}) => {
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (step) setNotes('');
  }, [step]);

  const isLock = step === 'cmLock';

  return (
    <Modal
      open={step !== null}
      onClose={busy ? () => {} : onClose}
      eyebrow={`Monthly run · ${monthDisplay}`}
      title={isLock ? 'Confirm payment lock' : 'Confirm ChannelMeter upload'}
      width={460}
      closeOnEsc={!busy}
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={isLock ? 'spark' : 'primary'}
            onClick={() => onConfirm(isLock ? undefined : notes || undefined)}
            disabled={busy}
          >
            {busy ? 'Confirming…' : isLock ? 'Confirm lock — irreversible' : 'Confirm upload'}
          </Button>
        </>
      }
    >
      <p style={styles.text}>
        {isLock
          ? `Confirm that payments for ${monthDisplay} have been locked in ChannelMeter. ` +
            'Locking is done in ChannelMeter itself and cannot be undone.'
          : `Confirm that you have uploaded both the Direct Sold and AdSense payout reports ` +
            `for ${monthDisplay} to ChannelMeter.`}
      </p>
      {!isLock && (
        <textarea
          style={styles.notes}
          placeholder="Optional notes (e.g. which CMS reports were uploaded)…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          disabled={busy}
        />
      )}
    </Modal>
  );
};

const styles: Record<string, React.CSSProperties> = {
  text: {
    margin: '0 0 14px',
    fontSize: 14,
    color: 'var(--fg-2)',
    lineHeight: 1.55,
  },
  notes: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 13,
    fontFamily: 'var(--font-sans)',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
};

export default ConfirmStageModal;
